import { action, computed, makeObservable, observable } from 'mobx';
import { UserRole } from '../../../apiClients/workspace/types';
import i18n from '../../../i18n';
import {
  EventInstanceStore,
  NotificationStore,
  UserStore,
  WorkspaceStore,
} from '../../../stores';
import { confirmDialogStore } from '../../../stores/ConfirmDialogStore';
import { Training } from '../types/calendarTypes';

export class ParticipantActionsStore {
  @observable training: Training;
  @observable currentUserId: string;

  userStore: UserStore;
  workspaceStore: WorkspaceStore;
  eventInstanceStore: EventInstanceStore;
  notificationStore: NotificationStore;

  constructor(
    training: Training,
    currentUserId: string,
    userStore: UserStore,
    workspaceStore: WorkspaceStore,
    eventInstanceStore: EventInstanceStore,
    notificationStore: NotificationStore
  ) {
    this.training = training;
    this.currentUserId = currentUserId;
    this.userStore = userStore;
    this.workspaceStore = workspaceStore;
    this.eventInstanceStore = eventInstanceStore;
    this.notificationStore = notificationStore;

    makeObservable(this);
  }

  // Computed properties

  /**
   * Finds the current user's participant record in this training
   * Includes both direct participation and replacement participation
   */
  @computed
  get currentUserParticipant() {
    return this.training.participants.find(
      (p) =>
        p.id === this.currentUserId || p.replacementFor === this.currentUserId
    );
  }

  /**
   * Checks if the current user is a direct participant in this training
   */
  @computed
  get isCurrentUserParticipant() {
    return (
      this.training.participants?.some((p) => p.id === this.currentUserId) ??
      false
    );
  }

  /**
   * Checks if any participant has cancelled their participation
   */
  @computed
  get hasCancelled() {
    return (
      this.training.participants?.some((p) => p.status === 'Cancelled') ?? false
    );
  }

  /**
   * Determines if the current user can replace a cancelled participant
   * Checks level difference and availability
   */
  @computed
  get canReplace() {
    if (
      this.training.status !== 'Active' ||
      !this.hasCancelled ||
      this.isCurrentUserParticipant ||
      this.training.participants.some(
        (p) => p.replacementFor === this.currentUserId
      )
    )
      return false;

    if (!this.training.level || !this.userStore.user?.skillLevel) return false;

    const levelDiff = Math.abs(
      this.training.level - this.userStore.user?.skillLevel
    );

    return (
      levelDiff <= (this.workspaceStore.maxLevelDifferenceForReplacement ?? 0)
    );
  }

  @computed
  get shouldShowReplaceButton() {
    return (
      this.training.status === 'Active' &&
      this.hasCancelled &&
      !this.isCurrentUserParticipant &&
      this.training.participants.every(
        (p) => p.replacementFor !== this.currentUserId
      )
    );
  }

  /**
   * Checks if the current user can confirm participation for this training
   * Only allows confirmation for active trainings and current user's participation
   */
  @computed
  get canConfirmForThisUser() {
    return (
      this.training.status === 'Active' &&
      !!this.currentUserParticipant &&
      this.currentUserParticipant.id === this.currentUserId
    );
  }

  /**
   * Checks if the current user can confirm participation right now
   * Only for pending or system-confirmed participants
   */
  @computed
  get canConfirmNowForThisUser() {
    if (!this.currentUserParticipant) return false;
    return (
      this.currentUserParticipant.status === 'Pending' ||
      this.currentUserParticipant.status === 'ConfirmedBySystem'
    );
  }

  /**
   * Checks if the current user has been replaced by someone else
   */
  @computed
  get isReplacedStatus() {
    return this.currentUserParticipant?.status === 'Replaced';
  }

  /**
   * Checks if the current user can confirm after cancelling
   * Only for cancelled participants who haven't been replaced
   */
  @computed
  get canConfirmAfterCancellation() {
    if (!this.currentUserParticipant) return false;
    return (
      this.currentUserParticipant.status === 'Cancelled' &&
      !this.isReplacedStatus
    );
  }

  /**
   * Checks if the current user can cancel their participation
   * Only for confirmed/pending participants in active trainings
   */
  @computed
  get canCancelParticipation() {
    const participant = this.currentUserParticipant;
    if (!participant) return false;

    return (
      (this.training.isCurrentUserParticipant ?? false) &&
      this.training.status === 'Active' &&
      ['Confirmed', 'ConfirmedBySystem', 'Pending'].includes(participant.status)
    );
  }

  /**
   * Checks if the current user is replacing someone else
   * Either as a replacement or being replaced
   */
  @computed
  get isReplacing() {
    const participant = this.currentUserParticipant;
    if (!participant) return false;

    return this.currentUserId === participant.replacementFor;
  }

  /**
   * Checks if the current user can cancel their replacement participation
   * Only for active trainings
   */
  @computed
  get canCancelReplacement() {
    return this.isReplacing && this.training.status === 'Active';
  }

  /**
   * Determines if confirmation action button should be shown
   * Shows for users who need to confirm or have been replaced
   */
  @computed
  get needsConfirmationAction() {
    return (
      this.canConfirmForThisUser &&
      (this.canConfirmNowForThisUser ||
        this.canConfirmAfterCancellation ||
        this.isReplacedStatus)
    );
  }

  /**
   * Checks if any action buttons should be shown at all
   * Combines all possible actions
   */
  @computed
  get needsAnyAction() {
    return (
      this.needsConfirmationAction ||
      this.canCancelParticipation ||
      this.canCancelReplacement
    );
  }

  // Actions

  /**
   * Confirms participation for the specified user
   * @param userId - The user ID to confirm participation for
   */
  @action
  async handleConfirm(userId: string) {
    try {
      await this.eventInstanceStore.confirmParticipation(
        this.training.id,
        userId,
        { actor: UserRole.Participant }
      );
    } catch (error) {
      console.error('Error in handleConfirm:', error);
    }
  }

  /**
   * Cancels the current user's participation in this training
   * Includes confirmation logic for penalties
   */
  async handleCancelParticipation() {
    try {
      // Calculate time until training start
      const trainingStartTime = new Date(this.training.startDateTime);
      const now = new Date();
      const minutesUntilStart = Math.round(
        (trainingStartTime.getTime() - now.getTime()) / 60000
      );

      // Get cancellation settings from workspace
      const freeCancellationDeadlineMinutes =
        this.workspaceStore.freeCancellationDeadlineMinutes ?? 24 * 60;
      const cancellationPenaltyPercent =
        this.workspaceStore.cancellationPenaltyPercent ?? 0;

      // Check if penalty applies (when cancellation is less than N hours before training)
      const penaltyApplies =
        minutesUntilStart < freeCancellationDeadlineMinutes;

      // Check if current user is replacing someone (can always cancel without confirmation)
      const isReplacing = this.isReplacing;

      // Check if confirmation is needed (only if penalty will actually be applied and user is not replacing)
      const needsConfirmation =
        !isReplacing &&
        cancellationPenaltyPercent !== null &&
        cancellationPenaltyPercent > 0 &&
        penaltyApplies;

      // Convert deadline to hours for display
      const deadlineHours = Math.ceil(freeCancellationDeadlineMinutes / 60);

      if (needsConfirmation) {
        // Show appropriate confirmation message
        let confirmMessage;
        let variant: 'danger' | 'warning' | 'info' = 'warning';

        if (penaltyApplies && cancellationPenaltyPercent > 0) {
          confirmMessage = i18n.t('calendar.cancelParticipationWarning', {
            deadline: deadlineHours,
            penalty: cancellationPenaltyPercent,
          });
          variant = 'warning';
        } else {
          confirmMessage = i18n.t('calendar.cancelParticipationNoPenalty');
          variant = 'info';
        }

        // Show confirmation dialog using global store
        confirmDialogStore.showCancellationConfirmation(
          confirmMessage,
          async () => {
            await this.eventInstanceStore.cancelParticipation(
              this.training.id,
              this.currentUserId,
              { actor: UserRole.Participant }
            );
          },
          variant
        );
      } else {
        // No confirmation needed, cancel directly
        await this.eventInstanceStore.cancelParticipation(
          this.training.id,
          this.currentUserId,
          { actor: UserRole.Participant }
        );
      }
    } catch (error) {
      console.error('Error in handleCancelParticipation:', error);
    }
  }

  /**
   * Handles replacing a cancelled participant
   * @returns Array of reason keys if replacement is not available, undefined if successful
   */
  async handleReplaceParticipant() {
    if (!this.canReplace) {
      const reasons = [];

      if (!this.hasCancelled) {
        reasons.push('calendar.noSpotsAvailable');
      } else {
        const cancelledParticipants =
          this.training.participants?.filter((p) => p.status === 'Cancelled') ??
          [];
        if (cancelledParticipants.length === 0) {
          reasons.push('calendar.noCancelledParticipants');
        } else {
          const cancelledLevel = this.training.level;
          const currentUserLevel = this.userStore.user?.skillLevel;

          if (!cancelledLevel || !currentUserLevel) {
            reasons.push('calendar.unknownLevelForReplacement');
          } else {
            const levelDiff = Math.abs(cancelledLevel - currentUserLevel);
            if (
              levelDiff >
              (this.workspaceStore.maxLevelDifferenceForReplacement ?? 0)
            ) {
              reasons.push('calendar.levelDifferenceTooHigh');
            }
          }
        }
      }

      const reasonMessages = reasons.map((reason) => i18n.t(reason));
      this.notificationStore.showInfo(
        reasonMessages.join('. ') || i18n.t('calendar.replaceNotAvailable')
      );

      return;
    }

    const cancelledParticipant = this.training.participants?.find(
      (p) => p.status === 'Cancelled'
    );
    if (cancelledParticipant && this.currentUserId) {
      try {
        await this.eventInstanceStore.replaceParticipant(
          this.training.id,
          cancelledParticipant.id,
          {
            replacingParticipantId: this.currentUserId,
            actor: UserRole.Participant,
          }
        );
      } catch (error) {
        console.error('Error in replace participant:', error);
      }
    }
  }
}
